import {
  AiResponseDetailDto,
  AiResponseDetailDtoContextStorageTypeEnum,
  AiResponseDetailDtoCurrentUserFeedbackEnum,
  AiResponseFeedbackInputDtoValueEnum,
  AiResponseSummaryDto,
  AiResponseSummaryDtoStatusEnum,
  PromptConfigOutputDto,
  PromptConfigOutputDtoScopeEnum,
} from '@kortxio/hub-api';
import { AxiosResponse } from 'axios';
import {
  AiResponseRow,
  AiResponsesClientFilters,
} from 'features/ai-responses/types';
import { parseApiHeader } from 'libs/api/helper';

export const filterByScope = (
  rows: AiResponseRow[],
  scope: PromptConfigOutputDtoScopeEnum | undefined
): AiResponseRow[] => {
  if (scope == null) {
    return rows;
  }

  return rows.filter((row) => row.promptConfigScope === scope);
};

export const filterByPrompt = (
  rows: AiResponseRow[],
  promptId: string | undefined
): AiResponseRow[] => {
  if (promptId == null) {
    return rows;
  }

  return rows.filter((row) => row.promptConfigId === promptId);
};

export const filterByVersion = (
  rows: AiResponseRow[],
  versionNumber: number | undefined
): AiResponseRow[] => {
  if (versionNumber == null) {
    return rows;
  }

  return rows.filter((row) => row.versionNumber === versionNumber);
};

export const filterByStatus = (
  rows: AiResponseRow[],
  status: AiResponseSummaryDtoStatusEnum | undefined
): AiResponseRow[] => {
  if (status == null) {
    return rows;
  }

  return rows.filter((row) => row.status === status);
};

export const searchByTitle = (
  rows: AiResponseRow[],
  query: string | undefined
): AiResponseRow[] => {
  if (query == null || query.trim() === '') {
    return rows;
  }

  const needle = query.toLowerCase();

  return rows.filter(
    (row) => row.outputTitle?.toLowerCase().includes(needle) ?? false
  );
};

export const applyClientFilters = (
  rows: AiResponseRow[],
  filters: AiResponsesClientFilters
): AiResponseRow[] => {
  return [
    (input: AiResponseRow[]) => filterByScope(input, filters.scope),
    (input: AiResponseRow[]) => filterByPrompt(input, filters.promptId),
    (input: AiResponseRow[]) => filterByVersion(input, filters.versionNumber),
    (input: AiResponseRow[]) => filterByStatus(input, filters.status),
    (input: AiResponseRow[]) => searchByTitle(input, filters.query),
  ].reduce((acc, step) => step(acc), rows);
};

export const formatVoteRatio = (
  thumbsUpCount: number,
  thumbsDownCount: number
): string => {
  if (thumbsUpCount + thumbsDownCount === 0) {
    return '—';
  }

  return `${thumbsUpCount} 👍 ${thumbsDownCount} 👎`;
};

export const resolveCurrentPromptName = (
  promptConfigId: string | undefined,
  snapshotName: string | undefined,
  prompts: PromptConfigOutputDto[] | undefined
): string | undefined => {
  if (promptConfigId != null && prompts != null) {
    const current = prompts.find((prompt) => prompt.id === promptConfigId);

    if (current != null) {
      return current.name;
    }
  }

  if (snapshotName != null && snapshotName !== '') {
    return snapshotName;
  }

  return undefined;
};

export const formatPromptWithVersion = (
  promptConfigId: string | undefined,
  snapshotName: string | undefined,
  versionNumber: number | null | undefined,
  prompts: PromptConfigOutputDto[] | undefined
): string => {
  const name = resolveCurrentPromptName(promptConfigId, snapshotName, prompts);

  if (name == null || versionNumber == null) {
    return '—';
  }

  return `${name} v${versionNumber}`;
};

type VoteCountable = {
  thumbsUpCount?: number;
  thumbsDownCount?: number;
};

const thumbsUpDelta = (
  feedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): number =>
  feedback === AiResponseDetailDtoCurrentUserFeedbackEnum.ThumbsUp ? 1 : 0;

const thumbsDownDelta = (
  feedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): number =>
  feedback === AiResponseDetailDtoCurrentUserFeedbackEnum.ThumbsDown ? 1 : 0;

const applyCountDelta = <T extends VoteCountable>(
  target: T,
  upDelta: number,
  downDelta: number
): T => ({
  ...target,
  thumbsUpCount: (target.thumbsUpCount ?? 0) + upDelta,
  thumbsDownCount: (target.thumbsDownCount ?? 0) + downDelta,
});

const upsertDeltas = (
  newValue: AiResponseFeedbackInputDtoValueEnum,
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): [number, number] => [
  thumbsUpDelta(newValue) - thumbsUpDelta(previousFeedback),
  thumbsDownDelta(newValue) - thumbsDownDelta(previousFeedback),
];

const clearDeltas = (
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): [number, number] => [
  -thumbsUpDelta(previousFeedback),
  -thumbsDownDelta(previousFeedback),
];

export const applyOptimisticVote = (
  detail: AiResponseDetailDto,
  newValue: AiResponseFeedbackInputDtoValueEnum
): AiResponseDetailDto => {
  const [up, down] = upsertDeltas(newValue, detail.currentUserFeedback);

  return {
    ...applyCountDelta(detail, up, down),
    currentUserFeedback: newValue,
  };
};

export const clearVoteOptimistically = (
  detail: AiResponseDetailDto
): AiResponseDetailDto => {
  const [up, down] = clearDeltas(detail.currentUserFeedback);

  return {
    ...applyCountDelta(detail, up, down),
    currentUserFeedback: undefined,
  };
};

export const restoreVoteSnapshot = (
  detail: AiResponseDetailDto,
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined,
  previousThumbsUpCount: number,
  previousThumbsDownCount: number
): AiResponseDetailDto => ({
  ...detail,
  currentUserFeedback: previousFeedback,
  thumbsUpCount: previousThumbsUpCount,
  thumbsDownCount: previousThumbsDownCount,
});

export const applyOptimisticVoteToSummaryRow = (
  row: AiResponseSummaryDto,
  newValue: AiResponseFeedbackInputDtoValueEnum,
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): AiResponseSummaryDto => {
  const [up, down] = upsertDeltas(newValue, previousFeedback);

  return applyCountDelta(row, up, down);
};

export const clearVoteOnSummaryRow = (
  row: AiResponseSummaryDto,
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined
): AiResponseSummaryDto => {
  const [up, down] = clearDeltas(previousFeedback);

  return applyCountDelta(row, up, down);
};

export const restoreSummaryRowVoteCounts = (
  row: AiResponseSummaryDto,
  previousThumbsUpCount: number,
  previousThumbsDownCount: number
): AiResponseSummaryDto => ({
  ...row,
  thumbsUpCount: previousThumbsUpCount,
  thumbsDownCount: previousThumbsDownCount,
});

export const downloadContextTooltip = (
  contextStorageType: AiResponseDetailDtoContextStorageTypeEnum | undefined
): string | undefined => {
  if (contextStorageType === AiResponseDetailDtoContextStorageTypeEnum.Stored) {
    return undefined;
  }

  if (
    contextStorageType === AiResponseDetailDtoContextStorageTypeEnum.Archived
  ) {
    return 'Context archived — contact admin';
  }

  return 'No context available for this run';
};

export const parseDownloadFilename = (
  response: AxiosResponse,
  fallback: string
): string => {
  const header = parseApiHeader('content-disposition', response);

  if (header == null || !header.includes('attachment')) {
    return fallback;
  }

  const filenameRegex = /filename[^\n;=]*=((["']).*?\2|[^\n;]*)/;
  const match = filenameRegex.exec(header);

  if (match == null) {
    return fallback;
  }

  return match[1].replace(/["']/g, '');
};
